// index.jsx — CommandPaletteRoot: the single global mount for the ⌘K palette.
// WL (Fable Hail Mary Wave 5). Mounted once in App.js. Owns open/close state, the
// Ctrl/⌘+K hotkey, focus capture+restore, and an unobtrusive launcher pill for
// mouse users. ADDITIVE: adds a new overlay + one quiet fixed affordance; it
// changes no existing navigation, route, or component.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { colors } from "../../utils";
import { fontFamily, field, radius, shadow, spacing, zIndex, focusRing } from "../../theme/tokens";
import { isUserLoggedIn } from "../../utils/helpers";
import { IconSearch } from "./icons";
import CommandPalette from "./CommandPalette";
import { SEARCH_PILL_RECHECK_EVENT } from "./searchPillAvoid";

// Detect the platform once for the correct modifier glyph in the hint.
const isMac =
  typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform || "");
const MOD_LABEL = isMac ? "⌘" : "Ctrl";

const Launcher = styled.button`
  position: fixed;
  left: ${spacing.lg}px;
  bottom: ${spacing.lg}px;
  z-index: ${zIndex.sticky};
  display: none; /* shown on pointer-capable, desktop-width screens only */
  align-items: center;
  gap: ${spacing.sm}px;
  min-height: 44px;
  padding: 0 14px;
  background: ${colors.white};
  border: 1px solid ${colors.lightBorder};
  border-radius: ${radius.pill}px;
  box-shadow: ${shadow.e1};
  color: ${field.muted};
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  /* A11y (WCAG 1.4.3 AA): resting opacity used to be 0.9, which blends
     field.muted (#667085, 4.97:1 on white) down to an effective ~#757e91
     (4.07:1) — below the 4.5:1 floor for the "Search" label text. Full
     opacity at rest keeps the label AA-compliant; hover/focus already
     strengthen the border/shadow for the "clearer on interaction" affordance. */
  opacity: 1;
  transition: opacity 150ms ease, box-shadow 150ms ease, border-color 150ms ease;

  &:hover {
    box-shadow: ${shadow.e2};
    border-color: #d7c4fb;
    color: ${field.ink};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing.shadow};
  }

  @media (min-width: 900px) and (pointer: fine) {
    display: inline-flex;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /* nem-medialib-searchwidget-overlaps-grid: fade out (instead of covering)
     whenever the pill's own fixed footprint intersects a
     [data-avoid-search-pill] element (e.g. a Media Library grid tile), and
     while the page is actively scrolling. Reappears once the footprint is
     clear. Purely visual — the Ctrl/⌘+K hotkey and the palette overlay are
     untouched, so keyboard users are never affected. */
  ${(p) =>
    p.$obscured &&
    `
    opacity: 0;
    pointer-events: none;
  `}
`;

const LauncherKbd = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  font-weight: 600;
  /* A11y (WCAG 1.4.3 AA): the CtrlK hint text (brand.primary #9B5AF8) on #f1e9fe
     was 3.42:1. PrimaryPurple700 (#8126DB) clears 4.5:1 (5.51:1). */
  color: ${colors.PrimaryPurple700};
  background: #f1e9fe;
  border-radius: ${radius.sm}px;
  padding: 2px 6px;
`;

// #4622b (Gordon, 2026-08-29): the padding-bottom "clearance" tried in
// nem-medialib-searchwidget-overlaps-grid only reserved space below the
// LAST grid row — it never stopped a mid-scroll row from landing under the
// pill's fixed footprint (measured live: pill at x16/y840 144x44 covering a
// grid tile's .meta text at scrollY=0). Hide-on-overlap checks the pill's
// real screen rect against any [data-avoid-search-pill] element instead.
function useSearchPillObscured(pillRef) {
  const [obscured, setObscured] = useState(false);

  useEffect(() => {
    let rafId = null;
    let settleTimer = null;

    const checkOverlap = () => {
      const el = pillRef.current;
      if (!el) return;
      const pillRect = el.getBoundingClientRect();
      const avoidEls = document.querySelectorAll("[data-avoid-search-pill]");
      let hit = false;
      for (const avoidEl of avoidEls) {
        const r = avoidEl.getBoundingClientRect();
        if (
          pillRect.right > r.left &&
          pillRect.left < r.right &&
          pillRect.bottom > r.top &&
          pillRect.top < r.bottom
        ) {
          hit = true;
          break;
        }
      }
      setObscured(hit);
    };

    const scheduleCheck = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(checkOverlap);
    };

    // Fade out immediately while scrolling (a mid-scroll frame can't be
    // measured reliably), then re-measure once scrolling settles.
    const handleScroll = () => {
      setObscured(true);
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(checkOverlap, 150);
    };

    scheduleCheck();
    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    window.addEventListener("resize", scheduleCheck);
    window.addEventListener(SEARCH_PILL_RECHECK_EVENT, scheduleCheck);

    // The mount-time check above can run before async grid content (lazy-
    // loaded thumbnails, MediaGrid.jsx's `loading="lazy"` <img>s) finishes
    // reflowing the page — no scroll/resize fires when that happens, so the
    // pill can be left stuck visible over a tile it now overlaps (MEASURED
    // live: scrollY=0, no scroll yet, pill still opacity 0.9 ~2s after nav
    // once thumbnails settle). Watch body layout so late reflows re-trigger
    // the same check.
    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleCheck);
      resizeObserver.observe(document.body);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("resize", scheduleCheck);
      window.removeEventListener(SEARCH_PILL_RECHECK_EVENT, scheduleCheck);
      if (rafId) cancelAnimationFrame(rafId);
      if (settleTimer) clearTimeout(settleTimer);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [pillRef]);

  return obscured;
}

// T22.236: another modal (e.g. the Create Wheel wizard, an MUI Dialog with
// aria-modal) already owns the screen. Opening the palette on top of it
// stacked a search overlay over the modal whose input could never take
// typing — the modal's focus trap pulled focus straight back. A closed MUI
// keepMounted modal sits under aria-hidden, so it does not count.
// review-1008-Frontend-7107: the bottom-docked GDPR cookie banner also carries
// role=dialog aria-modal=true, but the page behind it stays usable and its Tab
// trap never pulls focus back, so it must not block the palette (measured live
// web-stg main.94c5416c.js: Ctrl/⌘+K and the launcher pill did nothing until
// the user answered the banner). Same exclusion the Help FAB already makes.
const NON_BLOCKING_MODAL_SELECTOR = "[data-command-palette], .iyuga-consent-banner";

export const isOtherModalOpen = () => {
  if (typeof document === "undefined") return false;
  return Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]')).some(
    (el) => !el.matches(NON_BLOCKING_MODAL_SELECTOR) && !el.closest('[aria-hidden="true"]')
  );
};

const CommandPaletteRoot = () => {
  const [open, setOpen] = useState(false);
  const restoreRef = useRef(null);
  const pillRef = useRef(null);
  const obscured = useSearchPillObscured(pillRef);

  const openPalette = useCallback(() => {
    if (!isUserLoggedIn()) return;
    if (isOtherModalOpen()) return;
    restoreRef.current = document.activeElement;
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    // Restore focus to wherever the user was before opening.
    const el = restoreRef.current;
    if (el && typeof el.focus === "function") {
      requestAnimationFrame(() => el.focus());
    }
  }, []);

  // Global Ctrl/⌘+K toggle.
  useEffect(() => {
    const onKeyDown = (e) => {
      const key = (e.key || "").toLowerCase();
      if (key === "k" && (e.metaKey || e.ctrlKey) && !e.altKey) {
        if (!isUserLoggedIn()) return;
        e.preventDefault();
        const blocked = isOtherModalOpen();
        setOpen((prev) => {
          if (prev) {
            // toggling closed — mirror closePalette's focus restore
            const el = restoreRef.current;
            if (el && typeof el.focus === "function") {
              requestAnimationFrame(() => el.focus());
            }
            return false;
          }
          if (blocked) return false;
          restoreRef.current = document.activeElement;
          return true;
        });
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const loggedIn = isUserLoggedIn();

  // The launcher pill is fixed at the bottom-LEFT, where the full-screen
  // Messages inbox's last conversation row sits — the pill overlapped and
  // obscured that row's username (ISP-9999). Hide the pill on /messages* while
  // leaving the Ctrl/⌘+K hotkey and the palette overlay itself fully mounted
  // and functional. Mirrors the corner MiniDmDock's "O7" route-suppression.
  const { pathname } = useLocation();
  const onMessagesRoute = /^\/messages(\/|$)/.test(pathname || "");

  return (
    <>
      {loggedIn && !onMessagesRoute && (
        <Launcher
          ref={pillRef}
          type="button"
          onClick={openPalette}
          $obscured={obscured}
          aria-hidden={obscured || undefined}
          tabIndex={obscured ? -1 : undefined}
          aria-haspopup="dialog"
          aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
          aria-label={`Open command palette (${MOD_LABEL} K)`}
          title={`Search — ${MOD_LABEL}K`}
        >
          <IconSearch size={16} aria-hidden="true" />
          <span>Search</span>
          <LauncherKbd aria-hidden="true">{MOD_LABEL}K</LauncherKbd>
        </Launcher>
      )}
      <CommandPalette open={open} onClose={closePalette} />
    </>
  );
};

export default CommandPaletteRoot;
